/**
 * Sophie Chat Persistence Service
 * Handles CRUD operations for chat history in Firestore
 */
import { db } from '../../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp
} from 'firebase/firestore';
import { ChatMessage } from '../sophieService';

// Collection name
const SOPHIE_CHATS_COLLECTION = 'sophie_chats';

// Types
export interface SophieChat {
  id: string;
  userId: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

export interface SophieChatSummary {
  id: string;
  title: string;
  updatedAt: Date;
  messageCount: number;
}

// Helper to generate a clean title from first user message
const generateTitle = (firstMessage: string): string => {
  const maxLength = 35;
  
  // Clean up the message
  let cleaned = firstMessage
    .trim()
    .replace(/\n+/g, ' ')  // Replace newlines with spaces
    .replace(/\s+/g, ' ')   // Collapse multiple spaces
    .replace(/^(hi|hey|hello|good morning|good afternoon|good evening)[,!.\s]*/i, '') // Remove greetings
    .trim();
  
  // If empty after cleaning, use original
  if (!cleaned) {
    cleaned = firstMessage.trim().replace(/\n+/g, ' ').substring(0, maxLength);
  }
  
  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  
  // Truncate if needed
  if (cleaned.length <= maxLength) return cleaned;
  
  // Try to cut at a word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > maxLength * 0.6) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated.trim() + '...';
};

export const sophieChatService = {
  /**
   * Create a new chat session
   */
  async createChat(userId: string, firstMessage?: ChatMessage): Promise<string> {
    const now = serverTimestamp();
    const title = firstMessage ? generateTitle(firstMessage.content) : 'New Chat';
    
    const docRef = await addDoc(collection(db, SOPHIE_CHATS_COLLECTION), {
      userId,
      title,
      messages: firstMessage ? [firstMessage] : [],
      createdAt: now,
      updatedAt: now
    });
    
    return docRef.id;
  },

  /**
   * Update chat with new messages
   */
  async updateChat(chatId: string, messages: ChatMessage[], title?: string): Promise<void> {
    const chatRef = doc(db, SOPHIE_CHATS_COLLECTION, chatId);
    
    const update: any = {
      messages,
      updatedAt: serverTimestamp()
    };
    
    // Update title if provided (e.g., from first user message)
    if (title) {
      update.title = title;
    }
    
    await updateDoc(chatRef, update);
  },

  /**
   * Get all chats for a user (summaries only)
   */
  async getChats(userId: string): Promise<SophieChatSummary[]> {
    const q = query(
      collection(db, SOPHIE_CHATS_COLLECTION),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || 'Untitled',
        updatedAt: data.updatedAt?.toDate() || new Date(),
        messageCount: data.messages?.length || 0
      };
    });
  },

  /**
   * Get a single chat with all messages
   */
  async getChat(chatId: string): Promise<SophieChat | null> {
    const chatRef = doc(db, SOPHIE_CHATS_COLLECTION, chatId);
    const snapshot = await getDoc(chatRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const data = snapshot.data();
    return {
      id: snapshot.id,
      userId: data.userId,
      title: data.title || 'Untitled',
      messages: data.messages || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  },

  /**
   * Delete a chat
   */
  async deleteChat(chatId: string): Promise<void> {
    const chatRef = doc(db, SOPHIE_CHATS_COLLECTION, chatId);
    await deleteDoc(chatRef);
  },

  /**
   * Update chat title only
   */
  async updateTitle(chatId: string, title: string): Promise<void> {
    const chatRef = doc(db, SOPHIE_CHATS_COLLECTION, chatId);
    await updateDoc(chatRef, { 
      title,
      updatedAt: serverTimestamp()
    });
  }
};
